import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatToolbarModule } from '@angular/material/toolbar';

/** A block within an answer: an optional bold lead-in label plus body text. */
interface AnswerBlock {
  label?: string;
  text?: string;
}

interface FaqEntry {
  question: string;
  blocks: AnswerBlock[];
}

interface FaqSection {
  title?: string;
  entries: FaqEntry[];
}

/** A run of plain text, optionally a hyperlink (email -> mailto, URL -> href). */
interface TextSegment {
  text: string;
  href?: string;
}

/** Static FAQ page: the Privacy4Cars portal FAQ content as a grouped accordion. */
@Component({
  selector: 'app-faq',
  imports: [
    MatCardModule,
    MatExpansionModule,
    MatToolbarModule
  ],
  templateUrl: './faq.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './faq.component.scss'
})
export class FaqComponent {
  readonly sections: FaqSection[] = [
    {
      title: 'Privacy4Cars Portal FAQs',
      entries: [
        {
          question: 'What is the Privacy4Cars portal?',
          blocks: [
            {
              text: 'This portal lets you keep tabs on the personal data deletion workflow. You can see vehicles that are in your queue for processing and the details of the activity your operators are completing on your vehicles through the Privacy4Cars app. You can track the deletions being done in any time period you would like and for the locations of your choosing. Deletion certificates are linked and can be downloaded and shared. All reported data is downloadable and additional fields are available when downloading the Comma Separated Values (.csv) reports.'
            }
          ]
        },
        {
          question: 'What do the different tabs in the menu on the left mean?',
          blocks: [
            {
              label: 'Unprocessed VIN Report',
              text: 'The Unprocessed VIN report shows the vehicles that have arrived at your location but have not been scanned yet with the Privacy4Cars application. This lets you plan and focus your team\'s efforts and also helps you capture opportunities for performing the data deletion service.'
            },
            {
              label: 'Unprocessed Sold Report',
              text: 'Auto Auctions - If you\'re a part of the AutoIMS network, the "Unprocessed Sold Report" shows the vehicles that are Sold/Redeemed at your auction without being scanned with the Privacy4Cars application. For the entire list of FAQs related to AutoIMS refer to "AutoIMS FAQs" below.'
            },
            {
              text: 'Dealers - The "Unprocessed Sold Report" shows the vehicles that are Sold at your dealership without being scanned with the Privacy4Cars application.'
            },
            {
              label: 'Summary & Alerts',
              text: 'Summary & Alerts are meant to give you a very intuitive, visual representation of the data deletion attempts. The status of any particular vehicle deletion attempt can be Abandoned, Exception reported, Unsuccessful or Completed. If you have multiple auction sites, the reports group your view by state by default. You can click on any of the bar graphs to drill down into the particular state or site.'
            },
            {
              text: 'In this report you will also get important alerts. This is where you can see if any registered users are using the Privacy4Cars app outside their assigned geofences (this may be because users have been miscategorized, a site is not geofenced, or unauthorized activity is taking place). This is also where you can see if you have any "orphan users" i.e. users that do not appear to be fully configured in our system. If you notice unexpected activity or have questions, please contact support@privacy4cars.com'
            },
            {
              label: 'Activity By Location',
              text: 'The Activity By location dashboard displays all data as per the auction sites. It will also show the alerts for any vehicle processing performed outside the home location.'
            },
            {
              label: 'Activity By Date',
              text: 'The Activity By Date dashboard displays all your data across sites and states in one unified table. If you prefer to dig deep into the data and create your own filters and analysis, we suggest downloading the data for the time range required from here.'
            },
            {
              label: 'User Dashboard',
              text: 'The user dashboard allows you to view the users based on the hierarchy selection or provided to you. It will provide you the information of user role, type, assigned site and created date.'
            }
          ]
        },
        {
          question: 'What are the various AutoIMS statuses? What do they mean?',
          blocks: [
            { label: 'Pending', text: 'The deletion status is not sent to the AutoIMS system.' },
            { label: 'Pushed', text: 'The deletion status is sent to the AutoIMS system.' },
            { label: 'Published', text: 'The deletion status is verified with AutoIMS system after being pushed.' }
          ]
        },
        {
          question: 'How do I or my operators learn how to use the Privacy4Cars app?',
          blocks: [
            {
              text: 'Though we have made our app very intuitive to use even for first time users, we understand a good explanatory guide to the app is needed sometimes. We\'ve put together a set of explanatory videos at https://privacy4cars.com/training/. This should get you up to speed on using the app in no time.'
            },
            {
              text: 'If needed, you can ask to schedule a training session by reaching out to support@privacy4cars.com'
            }
          ]
        },
        {
          question: 'Who do I reach out to if I need assistance?',
          blocks: [
            {
              text: 'You can drop an email to support@privacy4cars.com. If you reach out to us during our working hours of 7:00AM to 6:00PM (Eastern Time), we will get back to you immediately. If you reach out to us outside of our working hours, we will still try to get back to you immediately but if we can, please be assured we will get back to you on the very next working day.'
            }
          ]
        },
        {
          question: 'What do I do if I have billing related queries?',
          blocks: [
            { text: 'Please write to us at accounts@privacy4cars.com with your inquiry.' }
          ]
        }
      ]
    },
    {
      title: 'AutoIMS FAQs',
      entries: [
        {
          question: 'Where is the Privacy4Cars information published on AutoIMS?',
          blocks: [
            {
              text: 'In the tabbed vehicle summary, under the "Condition" tab, there is a section titled "Privacy4Cars". This is where, for each VIN, we provide a summary of the latest status update. You can also build reports in AutoIMS to check on the status of your entire portfolio, or filter it according to various criteria as with all other AutoIMS reports.'
            },
            { text: 'Example of a VIN-specific Privacy4Cars record on AutoIMS' },
            {
              label: 'Other Record',
              text: 'This field tells us about any PII deletion that happened on the same vehicle previously. For instance, if vehicle X\'s PII was deleted when it was at auction A1, right deletion status will be displayed. If the same vehicle X is moved to some other auction A2, deletion status "Available For Service" will be displayed along with the "Other Record" having the PII deletion attempt details made at auction A1.'
            }
          ]
        },
        {
          question: 'How do I access my Privacy4Cars records on AutoIMS or set up a query?',
          blocks: [
            {
              text: 'You can watch the short video on Privacy4Cars\' training page (https://www.privacy4cars.com/training/) titled "how to access your records on AutoIMS". You can also reach out to the AutoIMS Support: they are fully proficient on the Privacy4Cars integration and can help you set up and manage that information.'
            }
          ]
        },
        {
          question: 'What data is published by Privacy4Cars on AutoIMS?',
          blocks: [
            { text: 'As of January 2021, we report the following fields for each VIN in AutoIMS:' },
            { label: 'Data Deletion Status', text: 'a short description of the status of the deletion. This field can assume any of the following values: Completed, Exception Reported, Unsuccessful, Abandoned, or Available For Service. More details in the question below.' },
            { label: 'Exception Type', text: 'this field is blank unless the status is "Exception Reported" (for instance, exception with the vehicle)' },
            { label: 'Exception Details', text: 'this field is blank unless the status is "Exception Reported" and provides further details as to why an exception was reported (for instance, key missing)' },
            { label: 'First Request Timestamp', text: 'date and time when information about that VIN first appeared in AutoIMS' },
            { label: 'Date Deletion Timestamp', text: 'date and time when the deletion was performed by the auction' },
            { label: 'Warranty for Data Deletion', text: 'The user can check the warranty certificate by clicking at the "Certificate" link. The certificate contains the details of PII deletion and the level of warranty offered by Privacy4Cars for this specific VIN (warranty levels depend on the level of subscription the auction contracted). Please refer to the arrow provided in the screenshot below (Warranty for Data Deletion) to see the warranty details.' },
            { text: 'Your auction has significantly more details about your vehicle history of deletion. If you need more information for a specific VIN, we encourage you to send an inquiry to your auction partner. If you send inquiries directly to support@privacy4cars.com, please copy your auction partner so we can keep them informed.' }
          ]
        },
        {
          question: 'How frequently Privacy4Cars updates the records on AutoIMS?',
          blocks: [
            {
              text: 'We exchange data with AutoIMS in batch mode, consequently there is a lag between the time a deletion has occurred and the time it is published on AutoIMS. As of January 11, 2021, we receive data from AutoIMS 4 times a day and we push data 5 times a day at 1 AM, 8 AM, 11 AM, 2 PM, 5 PM, and 9 PM EST We are working on increasing the frequency of exchanges. Your auction has access to our proprietary Privacy4Cars portal, which shows deletion metrics in real time, so they have the most up-to-date information.'
            }
          ]
        },
        {
          question: 'A vehicle has a blank status. What does it mean?',
          blocks: [
            { text: 'If your records are blank, it means we are not receiving or transmitting information with AutoIMS for that VIN. You should:' },
            { text: 'Make sure you signed up for a vehicle deletion service with your auction partner.' },
            { text: 'Make sure your auction partner is part of the Privacy4Cars network of auctions. We are the standard and exclusive partner of most large auction chains and several independents, with new locations being added regularly. To simplify your compliance, get standard records across your entire remarketing portfolio, and have the best chance to remove - and prove you have removed - the Nonpublic Personal Information stored in your company\'s vehicles for sale, ask all your auctions to use Privacy4Cars.' },
            { text: 'Make sure your auction notified us that you requested the deletion service. Privacy4Cars and AutoIMS have an onboarding process to turn on the data feeds for each consignor and each location. You can also let us know directly by sending an email to support@privacy4cars.com' },
            { text: 'If you believe you have met the criteria of (a), (b) and (c) above, but your records still show blank, send us an email at support@privacy4cars.com to open a ticket and we will look into a resolution.' },
            { text: 'Your auction has significantly more details about your vehicle history of deletion. If you need more information for a specific VIN, we encourage you to send an inquiry to your auction partner. If you send inquiries directly to support@Privacy4Cars.com, please copy your auction partner so we can keep them informed.' }
          ]
        },
        {
          question: 'A vehicle has "Available For Service" status. What does it mean?',
          blocks: [
            { text: 'When a consignor signs up for the service, and once we complete the onboarding with AutoIMS, we start receiving notifications when vehicles are consigned to a participating auction within the Privacy4Cars network. When a vehicle arrives at the auction, we tag it as "Available For Service" (AFS). This creates visibility over which VINs are awaiting for service, and makes it possible for both auctions and consignors to keep an eye on the "queue" of vehicles that have not been processed yet.' },
            { text: 'Once a VIN is scanned, it stops being AFS and is assigned an updated status depending on the outcome of that deletion (Completed, Exception Reported, Unsuccessful, or Abandoned). If you see vehicles in your portfolio with a AFS status that you do not think qualify for the service, please reach out to info@privacy4cars.com to provide you with an answer for your specific case.' }
          ]
        },
        {
          question: 'The auction says they have removed the Personal Information but the vehicle still shows "Available For Service" status. Why is that?',
          blocks: [
            { text: 'We update records on AutoIMS in batches multiple times a day. Consequently, it is possible that a vehicle was processed by the auction, but the data has not been published on AutoIMS yet. The auction has visibility over the status of the transmission to AutoIMS and will be able to provide more details.' }
          ]
        },
        {
          question: 'A vehicle has "Exception reported" status. What does it mean?',
          blocks: [
            { text: 'The unexpected happens regularly at used vehicle auctions. This is why we made it easy for operators to tag, within our process, if anything unusual happened that prevented them from removing the Personal Information from a vehicle. The most common exceptions reported are "Neither Bluetooth/Navigation" and "Unable to power (have key)". The full list of possible vehicle exceptions is listed in the screenshot to the left. This is the information that will be displayed on the AutoIMS portal under "Exception Details".' },
            { text: 'Some of these exceptions are worth of additional explanation:' },
            { text: 'If your vehicle is a base, possibly older model, it may not be equipped with systems capable of capturing personal Information. Auction personnel should be tagging these vehicles as a "Vehicle Exception - Neither Bluetooth/Navigation"' },
            { text: 'If the vehicle has an exception such as "Unable to power (have key)", "Key missing/not working" or "Breathalyzer locked" we recommend consignors and auctions discuss what measures, if any, should be taken to make a second attempt (e.g. after a key is cut, or a vehicle is jumped) at deleting the Personal Information.' },
            { text: 'Certain exceptions, albeit rare, may dramatically affect the value of a vehicle and may have not been captured in the Condition Report. For instance, a vehicle with an aftermarket infotainment may wholesale for a higher price, and a vehicle with a broken or missing head unit would be far less valuable than its well functioning comps. We encourage consignors and auctions to keep an eye on such exception codes as they may be able to improve sell rates, residuals, or reduce arbitrations.' }
          ]
        },
        {
          question: 'A vehicle has "Abandoned" status. What does it mean?',
          blocks: [
            { text: 'It is very uncommon, but if an operator starts the deletion process but does not complete it or does not confirm that she/he followed the steps we indicated and that those steps accurately depict his/her experience in the vehicle, we tag that VIN as "Abandoned". Abandoned VINs can be re-scanned and processed, free of charge, and their new status will be then sent to AutoIMS to provide the latest status. If you encounter Abandoned vehicles, have a discussion with your auction/auction personnel. In our upcoming app update we will have an improved flow and checks and balances to minimize abandonment.' }
          ]
        },
        {
          question: 'A vehicle has "Unsuccessful" status. What does it mean?',
          blocks: [
            { text: 'It is very uncommon, but some vehicles may be tagged as "Unsuccessful". This may mean that the field operator has determined we did not have good instructions for that vehicle by giving a 1 or 2 star rating in the submission screen. We can often determine, through the comments, photos, or with direct verification in the field, that a significant fraction of these "Unsuccessful" VINs really should have been tagged as "Vehicle exceptions" (the latest version of our app has an improved flow to minimize miscategorizations). That being said, there are tens of thousands of vehicle years, makes, models, and trims... and each of them may be equipped with different versions of hardware or firmware - it is occasionally possible that Privacy4Cars may not have a good procedure to delete the Personal Information from that vehicle. We continue to develop our database to make those instances even less frequent in the future, but we also backed our product with a Warranty to make sure that if this happens and you have a claim, you are financially covered. As of January 2021 we offer to our partners and resellers different tranches of warranty, ranging from $1,000,000 to $5,000,000 per claim. If you have a claim, please reach out to our CFO, James Bronstein, at james@privacy4cars.com.' }
          ]
        },
        {
          question: 'I see a warranty field. What does it mean?',
          blocks: [
            { text: 'At Privacy4Cars we stand behind our product with a financial backing. While different auctions may select different levels of coverage to be included with their subscription, our warranty, at minimum, includes a $1,000,000 warranty per claim. If you suffered a financial loss due to Privacy4Cars, have questions about the warranty program, or want to know what warranty level your auction selected, please reach out to our CFO, James Bronstein, at james@privacy4cars.com.' }
          ]
        }
      ]
    }
  ];

  /** Build an answer paragraph's inner HTML with emails/URLs as links. */
  answerHtml(text: string): string {
    return this.linkify(text)
      .map(seg => {
        const safe = this.escapeHtml(seg.text);
        return seg.href
          ? `<a href="${this.escapeHtml(seg.href)}" target="_blank" rel="noopener">${safe}</a>`
          : safe;
      })
      .join('');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Split text into plain runs and hyperlinks (emails and http(s) URLs). */
  private linkify(text: string): TextSegment[] {
    const re = /(https?:\/\/[^\s)]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
    const segments: TextSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ text: text.slice(lastIndex, match.index) });
      }
      let token = match[0];
      lastIndex = match.index + token.length;
      // Don't let trailing sentence punctuation become part of the link.
      let trailing = '';
      while (/[.,;:!?]$/.test(token)) {
        trailing = token.slice(-1) + trailing;
        token = token.slice(0, -1);
      }
      segments.push({ text: token, href: token.includes('@') ? `mailto:${token}` : token });
      if (trailing) {
        segments.push({ text: trailing });
      }
    }
    if (lastIndex < text.length) {
      segments.push({ text: text.slice(lastIndex) });
    }
    return segments;
  }
}
